import PropTypes from 'prop-types';
import { Link, useLocation, matchPath } from 'react-router-dom';

import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import IconButton from 'components/@extended/IconButton';
import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { getNavIconBoxSx, getNavItemSx, NAV } from '../../drawerNavStyles';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

export default function NavItem({ item, level = 1, isParents = false, setSelectedID }) {
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));

  const itemTarget = item.target ? '_blank' : '_self';
  const Icon = item.icon;
  const { pathname } = useLocation();
  const isSelected = !!matchPath({ path: item?.link ? item.link : item.url, end: false }, pathname);
  const isSubItem = level > 1;

  const itemHandler = () => {
    if (downLG) handlerDrawerOpen(false);
    if (isParents && setSelectedID) setSelectedID(item.id);
  };

  const iconBoxSx = getNavIconBoxSx(isSelected, isSubItem);

  const itemIcon = Icon && !isSubItem ? (
    <Box sx={iconBoxSx}>
      <Icon style={{ fontSize: '1.125rem' }} />
    </Box>
  ) : null;

  return (
    <Box sx={{ position: 'relative', mb: isSubItem ? 0 : 0.25 }}>
      <ListItemButton
        component={Link}
        to={item.url}
        target={itemTarget}
        disabled={item.disabled}
        selected={isSelected}
        sx={getNavItemSx({ isActive: isSelected, isSubItem, drawerOpen })}
        onClick={itemHandler}
      >
        {isSubItem && drawerOpen && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              flexShrink: 0,
              mr: 1.5,
              bgcolor: isSelected ? 'primary.main' : 'rgba(31, 91, 64, 0.25)',
              transition: 'all 0.2s ease'
            }}
          />
        )}

        {itemIcon && (
          <ListItemIcon
            sx={{
              minWidth: NAV.iconSize + 8,
              mr: drawerOpen ? 1.25 : 0,
              ...(!drawerOpen && {
                minWidth: 'auto',
                justifyContent: 'center'
              })
            }}
          >
            {itemIcon}
          </ListItemIcon>
        )}

        {(drawerOpen || (!drawerOpen && level !== 1)) && (
          <ListItemText
            primary={
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: isSubItem ? '0.8125rem' : '0.875rem',
                  color: 'inherit',
                  letterSpacing: '-0.01em',
                  lineHeight: 1.4
                }}
              >
                {item.title}
              </Typography>
            }
          />
        )}

        {drawerOpen && item.chip && (
          <Chip
            color={item.chip.color}
            variant={item.chip.variant}
            size={item.chip.size}
            label={item.chip.label}
            avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
            sx={{ height: 22, fontSize: '0.6875rem', fontWeight: 600 }}
          />
        )}
      </ListItemButton>

      {drawerOpen &&
        item?.actions?.map((action, index) => {
          const ActionIcon = action.icon;
          const callAction = action?.function;
          return (
            <IconButton
              key={index}
              {...(action.type === 'function' && {
                onClick: (event) => {
                  event.stopPropagation();
                  callAction();
                }
              })}
              {...(action.type === 'link' && {
                component: Link,
                to: action.url,
                target: action.target ? '_blank' : '_self'
              })}
              color="secondary"
              variant="outlined"
              sx={{
                position: 'absolute',
                top: 10,
                right: 16,
                zIndex: 1202,
                width: 22,
                height: 22
              }}
            >
              <ActionIcon style={{ fontSize: '0.625rem' }} />
            </IconButton>
          );
        })}
    </Box>
  );
}

NavItem.propTypes = {
  item: PropTypes.any,
  level: PropTypes.number,
  isParents: PropTypes.bool,
  setSelectedID: PropTypes.oneOfType([PropTypes.any, PropTypes.func])
};
